import React from 'react';

type ColumnType = 'avatar' | 'text' | 'badge' | 'image' | 'icon' | 'custom';

interface ColumnConfig {
  type: ColumnType;
  width?: string;
  height?: string;
  className?: string;
}

interface TableSkeletonProps {
  columns: ColumnConfig[];
  rows?: number;
  className?: string;
  rowClassName?: string;
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({
  columns,
  rows = 5,
  className = '',
  rowClassName = '',
}) => {
  const renderCellSkeleton = (col: ColumnConfig, rowIndex: number) => {
    const key = `${rowIndex}-${col.type}-${col.width}`;
    const baseClass = 'animate-pulse rounded';

    switch (col.type) {
      case 'avatar':
        return (
          <div
            key={key}
            className={`${baseClass} bg-gray-200 shrink-0`}
            style={{
              width: col.width || '32px',
              height: col.height || '32px',
              borderRadius: '50%',
            }}
          />
        );
      case 'text':
        return (
          <div
            key={key}
            className={`${baseClass} bg-gray-200`}
            style={{
              width: col.width || '60%',
              height: col.height || '12px',
            }}
          />
        );
      case 'badge':
        return (
          <div
            key={key}
            className={`${baseClass} bg-gray-200`}
            style={{
              width: col.width || '60px',
              height: col.height || '20px',
              borderRadius: '9999px',
            }}
          />
        );
      case 'image':
        return (
          <div
            key={key}
            className={`${baseClass} bg-gray-200`}
            style={{
              width: col.width || '40px',
              height: col.height || '40px',
              borderRadius: '4px',
            }}
          />
        );
      case 'icon':
        return (
          <div
            key={key}
            className={`${baseClass} bg-gray-200 shrink-0`}
            style={{
              width: col.width || '16px',
              height: col.height || '16px',
              borderRadius: '2px',
            }}
          />
        );
      case 'custom':
        return (
          <div
            key={key}
            className={`${baseClass} bg-gray-200`}
            style={{
              width: col.width || '40px',
              height: col.height || '16px',
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="w-full overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="border-b border-[#D4D4D4]">
              {columns.map((col, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-left"
                  style={{ width: col.width }}
                >
                  <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr
                key={rowIndex}
                className={`border-b border-[#F0EAFF] ${rowClassName}`}
              >
                {columns.map((col, colIndex) => (
                  <td
                    key={colIndex}
                    className="px-4 py-3"
                    style={{ width: col.width }}
                  >
                    {renderCellSkeleton(col, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableSkeleton;
